import { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  CloseOutlined,
} from "@ant-design/icons";
import { ThemeContext } from "../context/ThemeContext";
import "./TwoFactorAuthPopup.css";
import logoShort from "../assets/logoShort.png";
import { isAdmin } from "../utils/roleUtils";
import headerIcon from "../assets/img/header_icon.png";
import enhancedIcon from "../assets/img/Enhanced.png";
import realtimeIcon from "../assets/img/Real_time.png";
import secureIcon from "../assets/img/Secure.png";
import industryIcon from "../assets/img/Industry.png";
import checkIcon from "../assets/img/2fa_check.png";

const TwoFactorAuthPopup = ({ visible, onClose = () => {}, email, onSkip }) => {
  const navigate = useNavigate();
  const { isDark } = useContext(ThemeContext);
  const [isLoading, setIsLoading] = useState(false);

  const handleSkip2FA = async () => {
    setIsLoading(true);
    try {
      // Call parent's skip handler
      if (onSkip) {
        await onSkip();
      }
      
      // Call onClose if it's a function
      if (typeof onClose === 'function') {
        onClose();
      } else {
        console.warn("TwoFactorAuthPopup - onClose is not a function in handleSkip2FA:", onClose);
      }

    } catch (error) {
      console.error("Error skipping 2FA:", error);
      toast.error("Failed to skip 2FA setup", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: isDark ? "dark" : "light",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinue2FA = () => {
    const isUserAdmin = isAdmin();
    console.log("TwoFactorAuthPopup - User is admin:", isUserAdmin);
    
    // Call onClose if it's a function
    if (typeof onClose === 'function') {
      onClose();
    } else {
      console.warn("TwoFactorAuthPopup - onClose is not a function:", onClose);
    }

    if (isUserAdmin) {
      console.log("TwoFactorAuthPopup - Navigating to admin 2FA page");
      navigate("/admin/2fa", {
        state: {
          email: email,
        },
      });
    } else {
      console.log("TwoFactorAuthPopup - Navigating to regular user 2FA page");
      navigate("/d/2fa", {
        state: {
          email: email,
        },
      });
    }
  };

  if (!visible) return null;

  return (
    <div className="twofa-popup-overlay">
      <div className="twofa-popup-modal">
        <div className="twofa-popup-content">
          {/* Header with close button */}
          <div className="twofa-popup-header">
            <button className="close-btn" onClick={() => {
              if (typeof onClose === 'function') {
                onClose();
              } else {
                console.warn("TwoFactorAuthPopup - onClose is not a function in close button:", onClose);
              }
            }}>
              <CloseOutlined />
            </button>
          </div>

          {/* Main content */}
          <div className="twofa-popup-body">
            {/* Header Icon */}
            <div className="twofa-shield-icon">
              <img src={headerIcon} alt="Security" className="header-icon-img" />
            </div>

            {/* Title */}
            <h2 className="twofa-title">Two-factor Authentication</h2>

            {/* Subtitle */}
            <p className="twofa-subtitle">
              Enable Two-Factor Authentication (2FA) to add an extra layer of
              security to your SimplyBond account.
            </p>

            {/* Features List */}
            <div className="twofa-features">
              <div className="twofa-feature">
                <div className="twofa-feature-icon enhanced">
                  <img src={enhancedIcon} alt="Enhanced Protection" className="feature-icon-img" />
                </div>
                <span className="twofa-feature-text">Enhanced Account Protection</span>
                <div className="twofa-feature-check">
                  <img src={checkIcon} alt="Check" className="check-icon-img" />
                </div>
              </div>
              <div className="twofa-feature">
                <div className="twofa-feature-icon monitoring">
                  <img src={realtimeIcon} alt="Real-time Monitoring" className="feature-icon-img" />
                </div>
                <span className="twofa-feature-text">Real-time Security Monitoring</span>
                <div className="twofa-feature-check">
                  <img src={checkIcon} alt="Check" className="check-icon-img" />
                </div>
              </div>
              <div className="twofa-feature">
                <div className="twofa-feature-icon access">
                  <img src={secureIcon} alt="Secure Access" className="feature-icon-img" />
                </div>
                <span className="twofa-feature-text">Secure Access Control</span>
                <div className="twofa-feature-check">
                  <img src={checkIcon} alt="Check" className="check-icon-img" />
                </div>
              </div>
              <div className="twofa-feature">
                <div className="twofa-feature-icon industry">
                  <img src={industryIcon} alt="Industry Standard" className="feature-icon-img" />
                </div>
                <span className="twofa-feature-text">Industry Standard Security</span>
                <div className="twofa-feature-check">
                  <img src={checkIcon} alt="Check" className="check-icon-img" />
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="twofa-popup-actions">
            <button
              type="button"
              className="skip-btn"
              onClick={handleSkip2FA}
              disabled={isLoading}
            >
              Skip 2FA Setup
            </button>
            <button
              type="button"
              className="continue-btn"
              onClick={handleContinue2FA}
              disabled={isLoading}
            >
              Continue 2FA Setup
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorAuthPopup;
